"use client";

import { AboutPageQueryResult } from "@/sanity.types";
import { useState, useEffect, useRef } from "react";
import BadgeRoundedSquare from "@/app/(site)/_components/utils/badge-rounded-square/BadgeRoundedSquare";
import { motion, AnimatePresence } from "framer-motion";

type YellowBlocksSliderProps = {
	blocks: NonNullable<AboutPageQueryResult>["coreOfEverythingWeDo"]["blocks"];
};

export default function YellowBlocksSlider({ blocks }: YellowBlocksSliderProps) {
	const [activeSlide, setActiveSlide] = useState(0);
	const [direction, setDirection] = useState(0); // 1 for down, -1 for up
	const sectionRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleScroll = () => {
			if (!sectionRef.current) return;

			// Find parent section with height
			const section = sectionRef.current.closest('section[id="coreOfEverythingWeDo"]') as HTMLElement;
			if (!section) return;

			const sectionRect = section.getBoundingClientRect();
			const sectionTop = sectionRect.top;
			const sectionHeight = sectionRect.height;
			const viewportHeight = window.innerHeight;

			// Calculate progress in the section
			const scrollProgress = Math.max(0, Math.min(1, -sectionTop / (sectionHeight - viewportHeight)));

			// Calculate which slide should be active
			const slideIndex = Math.floor(scrollProgress * blocks.length);
			const clampedIndex = Math.max(0, Math.min(blocks.length - 1, slideIndex));

			if (clampedIndex !== activeSlide) {
				setDirection(clampedIndex > activeSlide ? 1 : -1);
				setActiveSlide(clampedIndex);
			}
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [activeSlide, blocks.length]);

	const currentBlock = blocks[activeSlide];

	if (!currentBlock) return null;

	const slideVariants = {
		enter: (direction: number) => ({
			y: direction > 0 ? 50 : -50,
			opacity: 0,
		}),
		center: {
			y: 0,
			opacity: 1,
		},
		exit: (direction: number) => ({
			y: direction < 0 ? 50 : -50,
			opacity: 0,
		}),
	};

	return (
		<div ref={sectionRef} className="relative overflow-hidden">
			<motion.div className="bg-olive-green-light relative min-h-[25.625rem] rounded-lg pt-5 pr-5 pb-7.5 pl-5 sm:min-h-[23.125rem]">
				<div className="flex flex-col">
					<div className="flex flex-col gap-5">
						<AnimatePresence mode="popLayout" custom={direction}>
							<motion.div
								key={`badge-${activeSlide}`}
								custom={direction}
								variants={slideVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{
									duration: 0.4,
									ease: "easeInOut",
								}}
							>
								<BadgeRoundedSquare
									text={String(activeSlide + 1).padStart(2, "0")}
									backgroundColor="bg-olive-green-middle"
									textColor="text-olive-green-light"
									className="transition-all duration-300"
								/>
							</motion.div>
						</AnimatePresence>

						<AnimatePresence mode="popLayout" custom={direction}>
							<motion.div
								key={`content-${activeSlide}`}
								custom={direction}
								variants={slideVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{
									duration: 0.4,
									ease: "easeInOut",
								}}
								className="flex flex-col gap-5"
							>
								<div className="flex flex-col">
									<p className="text-cap-m sm:text-cap-l-badge text-graphite-black transition-all delay-75 duration-300">
										{currentBlock.headline}
									</p>
								</div>

								<ul className="flex flex-col gap-5">
									{currentBlock.texts.map((text, index) => (
										<li key={index} className="flex items-baseline gap-x-3">
											<span className="bg-graphite-black inline-block h-2 w-2 shrink-0 rounded-full"></span>
											<span className="text-body-s sm:text-body-m text-graphite-black/70 whitespace-pre-wrap transition-all delay-100 duration-300">
												{text.text}
											</span>
										</li>
									))}
								</ul>
							</motion.div>
						</AnimatePresence>
					</div>
				</div>
			</motion.div>
		</div>
	);
}
